// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './HistoryProcessedProducts.module.scss';
import { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeNumber } from 'util/commons';
import { ProcessedProductHistoryItem } from 'util/Types';
import ResponsiveHistoryTable, {
  CustomColumn,
} from 'components/molecules/ResponsiveHistoryTable';
import CustomPagination from 'components/atoms/CustomPagination';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { useFetchProcessedProductHistoryQuery } from 'apis/compressions_user/compressionUserApi';
import { getFormatDateWithDeviceTimezone } from 'ts/formatDate';
import Popover from 'components/atoms/Popover';
import BigNumber from 'bignumber.js';
import QRCode from 'react-qr-code';
import Portal from 'components/atoms/Portal';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { toast } from 'react-toastify';

const PRINT_WIDTH = 640;

const PAGE_SIZE = 20;

export const HistoryProcessedProducts = ({
  onAppliedDateRangeChange,
}: {
  onAppliedDateRangeChange?: (range: [string, string] | undefined) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [dateRange, setDateRange] = useState<[string, string]>();
  const [appliedDateRange, setAppliedDateRange] = useState<[string, string]>();

  const { data, refetch } = useFetchProcessedProductHistoryQuery({
    page: currentPage + 1,
    take: PAGE_SIZE,
    dateFrom: appliedDateRange?.[0],
    dateTo: appliedDateRange?.[1],
  });

  const rows = data?.data ?? [];
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);
  const [activeQrRow, setActiveQrRow] =
    useState<ProcessedProductHistoryItem | null>(null);
  const qrModalRef = useRef<HTMLDivElement>(null);
  const qrModalContentRef = useRef<HTMLDivElement>(null);

  const handleQrModalClick = (e: React.MouseEvent) => {
    if (qrModalRef.current && e.target === qrModalRef.current) {
      setActiveQrRow(null);
    }
  };

  const isAndroid = () => /Android/i.test(navigator.userAgent);

  const handlePrintQr = () => {
    if (!qrModalContentRef.current || !activeQrRow) {
      toast.error(t('history_emission_card.pdf_export_error'));
      return;
    }
    if (isAndroid()) {
      window?.printCompressionsLabel?.(
        JSON.stringify({
          id: activeQrRow.id,
          qrCode: activeQrRow.qrCode,
          weight: normalizeNumber({ value: activeQrRow.weight }),
          type: activeQrRow.kind,
          materialName: activeQrRow.materialName,
          registDatetime: getFormatDateWithDeviceTimezone(
            activeQrRow.registDatetime
          ),
          measuredWeight: normalizeNumber({
            value: activeQrRow.measuredWeight,
          }),
          containerWeight: normalizeNumber({
            value: activeQrRow.containerWeight,
          }),
        }),
        i18n.language
      );
      setActiveQrRow(null);
      return;
    }
    try {
      const cloned = qrModalContentRef.current.cloneNode(true) as HTMLElement;
      cloned.style.backgroundColor = '#FFFFFF';
      cloned.querySelectorAll('span, div, p').forEach((el) => {
        (el as HTMLElement).style.color = '#000000';
      });
      const button = cloned.querySelector('button');
      if (button) button.remove();
      cloned.style.position = 'absolute';
      cloned.style.left = '-9999px';
      document.body.appendChild(cloned);
      const exportHeight = Math.max(
        PRINT_WIDTH * Math.sqrt(2),
        cloned.scrollHeight
      );
      html2canvas(cloned, {
        backgroundColor: '#FFFFFF',
        width: PRINT_WIDTH,
        height: exportHeight,
        windowWidth: PRINT_WIDTH,
        windowHeight: exportHeight,
        scale: 2,
      })
        .then((canvas) => {
          if (document.body.contains(cloned)) document.body.removeChild(cloned);
          const pdf = new jsPDF('portrait', 'px', [
            canvas.width,
            canvas.height,
          ]);
          pdf.addImage(
            canvas.toDataURL('image/png'),
            'PNG',
            0,
            0,
            canvas.width,
            canvas.height
          );
          pdf.save(`processed-product-qr-${activeQrRow.id}.pdf`);
          toast.success(t('history_emission_card.pdf_export_success'));
          setActiveQrRow(null);
        })
        .catch(() => {
          if (document.body.contains(cloned)) document.body.removeChild(cloned);
          toast.error(t('history_emission_card.pdf_export_error'));
        });
    } catch {
      toast.error(t('history_emission_card.pdf_export_error'));
    }
  };

  const handleSearch = () => {
    const isSameRange =
      dateRange?.[0] === appliedDateRange?.[0] &&
      dateRange?.[1] === appliedDateRange?.[1];

    setCurrentPage(0);
    setAppliedDateRange(dateRange);
    onAppliedDateRangeChange?.(dateRange);

    if (isSameRange) {
      refetch();
    }
  };

  const columns: CustomColumn<ProcessedProductHistoryItem>[] = useMemo(
    () => [
      {
        Header: t(
          'histories.label_processing_registered_date_and_time'
        ) as string,
        accessor: (row) => row.registDatetime,
        Cell: ({ value }) => <>{getFormatDateWithDeviceTimezone(value)}</>,
      },
      {
        Header: t('common_history_field.material_name') as string,
        accessor: (row) => row.materialName,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.type') as string,
        accessor: (row) => row.kind,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('histories.label_quality_grade') as string,
        accessor: (row) => row.grade,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('pre_regist_resource_select.input_weight') as string,
        accessor: 'inputResourceWeight',
        Cell: ({ value }) => <>{normalizeNumber({ value }) + 'kg'}</>,
      },
      {
        Header: t('common_history_field.weight') as string,
        accessor: (row) => row.weight,
        Cell: ({ value, row }) => {
          const displayValue = normalizeNumber({ value }) + 'kg';

          if (Number(value) > Number(row.original.inputResourceWeight)) {
            return (
              <Popover
                content={t('common_history_field.exceeded_input_weight', {
                  value: normalizeNumber({
                    value: BigNumber(value)
                      .minus(BigNumber(row.original.inputResourceWeight))
                      .toString(),
                  }),
                })}
                positions={['top']}
                containerStyle={{ zIndex: '1000' }}
              >
                <span
                  style={{
                    color: '#f75354',
                  }}
                >
                  {displayValue}
                </span>
              </Popover>
            );
          }

          return <>{displayValue}</>;
        },
      },
      {
        Header: t('common_history_field.registration_id') as string,
        accessor: (row) => row.id,
        Cell: ({ value }) => <>{value || '-'}</>,
      },
      {
        Header: t('histories.label_status') as string,
        accessor: (row) => row.status,
        Cell: ({ value }) => (
          <>{t(`histories.processed_product_status.${value}`, value) || '-'}</>
        ),
      },
      {
        Header: 'QR',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'qrCode' as any,
        className: styles.centerColumn,
        Cell: ({ row }) =>
          row.original.qrCode ? (
            <div className={styles.printLabelButton}>
              <button
                type="button"
                onClick={() => setActiveQrRow(row.original)}
              >
                {t('history_emission_card.card_print_qr_label_button')}
              </button>
            </div>
          ) : (
            <>-</>
          ),
      },
    ],
    [t]
  );

  return (
    <div className={styles.historyProcessedProducts}>
      <div className={styles.header}>
        <TableFilterCalendar
          wrapperClassName={styles.dateFilter}
          containerClassName={styles.dateFilterInner}
          expanded={{
            placeholder: t('common.placeholder.select_date_range'),
            icon: <IconCalendarTablet />,
          }}
          portalPositionPoint={(ref) => ref}
          endDateRequired
          value={dateRange}
          onChange={setDateRange}
        />
        <button className={styles.searchButton} onClick={handleSearch}>
          {t('common.button.search')}
        </button>
      </div>

      <div className={styles.content}>
        <ResponsiveHistoryTable<ProcessedProductHistoryItem>
          data={rows}
          columns={columns}
          scrollResetDependencies={[currentPage]}
        />
      </div>

      {totalPages > 0 && (
        <div className={styles.paginationContainer}>
          <CustomPagination
            pageCount={totalPages}
            onPageChange={({ selected }) => setCurrentPage(selected)}
            forcePage={currentPage}
          />
        </div>
      )}

      {activeQrRow && activeQrRow.qrCode && (
        <Portal>
          <div
            className={styles.qrModal}
            ref={qrModalRef}
            onClick={handleQrModalClick}
          >
            <div ref={qrModalContentRef} className={styles.qrModalContent}>
              <div className={styles.qrModalMainContainer}>
                <div className={styles.qrCodeContainer}>
                  <div className={styles.qrCodeBackground}>
                    <QRCode
                      value={activeQrRow.qrCode}
                      size={200}
                      className={styles.qrCodeStyle}
                    />
                  </div>
                </div>
                <div className={styles.qrModalRightContent}>
                  <div className={styles.qrModalInfoRowWeight}>
                    <span className={styles.qrModalLabelWeight}>
                      {t('common_history_field.weight')}
                    </span>
                    <span className={styles.qrModalValueWeight}>
                      {normalizeNumber({ value: activeQrRow.weight })} kg
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('common_history_field.registration_id')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {activeQrRow.id}
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('histories.label_processing_registered_date_and_time')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {getFormatDateWithDeviceTimezone(
                        activeQrRow.registDatetime
                      )}
                    </span>
                  </div>
                  <div className={styles.qrModalInfoRow}>
                    <span className={styles.qrModalLabel}>
                      {t('common_history_field.material_name')}
                    </span>
                    <span className={styles.qrModalValue}>
                      {activeQrRow.materialName}
                    </span>
                  </div>
                  {activeQrRow.kind && (
                    <div className={styles.qrModalInfoRow}>
                      <span className={styles.qrModalLabel}>
                        {t('common_history_field.type')}
                      </span>
                      <span className={styles.qrModalValue}>
                        {activeQrRow.kind}
                      </span>
                    </div>
                  )}
                  {activeQrRow.grade && (
                    <div className={styles.qrModalInfoRow}>
                      <span className={styles.qrModalLabel}>
                        {t('histories.label_quality_grade')}
                      </span>
                      <span className={styles.qrModalValue}>
                        {activeQrRow.grade}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handlePrintQr}
                className={styles.qrModalPrintButton}
              >
                {t('history_emission_card.print_label_button')}
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};
